import { useState, useMemo, useCallback, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import { Button, Input, Select, Space, Table, Tag, Tooltip, Popconfirm, Typography, Badge } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { DeleteOutlined, EditOutlined, PlusOutlined, ThunderboltOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useDelete, getDefaultFilter, useGo } from "@refinedev/core";
import { getPriorityColor } from "@/utils/formatters";

const { Text } = Typography;

type RoutingRule = {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  is_fallback: boolean;
  priority: number;
  conditions: any;
  generation_config: any;
  matched_count: number;
  last_matched_at?: string;
  created_at: string;
  updated_at: string;
};

export default function StrategiesList() {
  const go = useGo();
  const {
    tableProps,
    filters,
    setFilters,
    tableQuery,
  } = useTable<RoutingRule>({
    resource: "strategies",
    pagination: { mode: "server", pageSize: 20 },
    sorters: { initial: [{ field: "priority", order: "desc" }] },
    filters: {
      initial: [
        { field: "name", operator: "contains", value: undefined },
        { field: "is_active", operator: "eq", value: undefined },
        { field: "is_fallback", operator: "eq", value: undefined },
      ],
    },
    syncWithLocation: true,
  });

  const { mutate: deleteRule } = useDelete();

  const [searchDraft, setSearchDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState<boolean | undefined>();
  const [fallbackDraft, setFallbackDraft] = useState<boolean | undefined>();

  const appliedSearchValue = useMemo(() => {
    const f = getDefaultFilter("name", filters);
    return typeof f?.value === "string" ? f.value : "";
  }, [filters]);

  const appliedStatusValue = useMemo(() => {
    const f = getDefaultFilter("is_active", filters);
    return (f?.value as boolean | undefined) ?? undefined;
  }, [filters]);

  const appliedFallbackValue = useMemo(() => {
    const f = getDefaultFilter("is_fallback", filters);
    return (f?.value as boolean | undefined) ?? undefined;
  }, [filters]);

  // Синхронизация draft-состояний с примененными фильтрами
  useEffect(() => {
    setSearchDraft(appliedSearchValue);
  }, [appliedSearchValue]);

  useEffect(() => {
    setStatusDraft(appliedStatusValue);
  }, [appliedStatusValue]);

  useEffect(() => {
    setFallbackDraft(appliedFallbackValue);
  }, [appliedFallbackValue]);

  const updateFilters = useCallback(
    (search: string, status?: boolean, fallback?: boolean) => {
      const trimmed = search.trim();
      setFilters(
        [
          { field: "name", operator: "contains", value: trimmed || undefined },
          { field: "is_active", operator: "eq", value: status !== undefined ? status : undefined },
          { field: "is_fallback", operator: "eq", value: fallback !== undefined ? fallback : undefined },
        ],
        "replace"
      );
      // Reset to page 1 handled by tableProps
    },
    [setFilters]
  );

  const handleDelete = useCallback(
    (id: string, name: string) => {
      deleteRule(
        {
          resource: "strategies",
          id,
          successNotification: () => ({
            message: `Правило "${name}" удалено`,
            description: "",
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            // Simply refetch after deletion, pagination will auto-adjust
            tableQuery.refetch();
          },
        }
      );
    },
    [deleteRule, tableQuery]
  );

  const renderMode = (config: any) => {
    const mode = config?.mode;
    const colors: Record<string, string> = {
      manual: "red",
      ai: "green",
      template: "blue",
    };
    const labels: Record<string, string> = {
      manual: "Ручной",
      ai: "AI",
      template: "Шаблон",
    };
    return <Tag color={colors[mode] || "default"}>{labels[mode] || mode || "не указан"}</Tag>;
  };

  const columns: ColumnsType<RoutingRule> = [
    {
      title: "Приоритет",
      dataIndex: "priority",
      key: "priority",
      width: 120,
      sorter: true,
      defaultSortOrder: "descend",
      render: (priority, record) => (
        <Space>
          <Badge count={priority} style={{ backgroundColor: getPriorityColor(priority) }} />
          {record.is_fallback && (
            <Tooltip title="Fallback стратегия">
              <ThunderboltOutlined style={{ color: "#faad14" }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Название",
      dataIndex: "name",
      filteredValue: appliedSearchValue ? [appliedSearchValue] : null,
      filterIcon: <SearchOutlined style={{ color: appliedSearchValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 260 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            allowClear
            placeholder="Поиск по названию"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onPressEnter={() => {
              updateFilters(searchDraft, statusDraft, fallbackDraft);
              close();
            }}
            style={{ marginBottom: 8 }}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, statusDraft, fallbackDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setSearchDraft("");
              updateFilters("", statusDraft, fallbackDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Режим",
      key: "mode",
      width: 130,
      render: (_, record) => renderMode(record.generation_config),
    },
    {
      title: "Статус",
      dataIndex: "is_active",
      width: 120,
      align: "center",
      filteredValue: appliedStatusValue !== undefined ? [appliedStatusValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedStatusValue !== undefined ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 180 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: '100%', marginBottom: 8 }}
            value={statusDraft}
            onChange={(value) => setStatusDraft(value)}
            options={[
              { value: true, label: "Активно" },
              { value: false, label: "Неактивно" },
            ]}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, statusDraft, fallbackDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setStatusDraft(undefined);
              updateFilters(searchDraft, undefined, fallbackDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (active: boolean) =>
        active ? (
          <Tag color="success">Активно</Tag>
        ) : (
          <Tag color="default">Неактивно</Tag>
        ),
    },
    {
      title: "Срабатываний",
      dataIndex: "matched_count",
      width: 130,
      align: "right",
      render: (count) => <Text>{count || 0}</Text>,
    },
    {
      title: "Последнее срабатывание",
      dataIndex: "last_matched_at",
      width: 150,
      sorter: true,
      render: (date) =>
        date ? (
          <Text type="secondary">{dayjs(date).format("YYYY-MM-DD")}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
      align: "center"
    },
    {
      title: "Действия",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                go({
                  to: { resource: "strategies", action: "edit", id: record.id },
                  type: "push",
                });
              }}
              aria-label="Редактировать"
            />
          </Tooltip>
          {!record.is_fallback && (
            <Popconfirm
              title="Удалить правило"
              description={`Вы уверены, что хотите удалить "${record.name}"?`}
              onConfirm={() => handleDelete(record.id, record.name)}
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Удалить">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Удалить"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="Правила маршрутизации"
        headerButtons={() => (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              go({
                to: {
                  resource: "strategies",
                  action: "create",
                },
                type: "push",
              })
            }
          >
            Создать правило
          </Button>
        )}
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          bordered
          size="middle"
          scroll={{ x: 1200 }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} правил`,
          }}
        />
      </List>
    </>
  );
}
